import {AgentIcebreakers} from '@github-ui/copilot-shared-ui/components/AgentIcebreakers'
import type {IcebreakerOption} from '@github-ui/copilot-shared-ui/components/icebreaker'
import {updatedThreadSuggestions} from '@github-ui/copilot-chat-helpers/prompts'
import type {GeneratedSuggestion, GeneratedSuggestionIconKey} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {
  BeakerIcon,
  ChecklistIcon,
  CodeReviewIcon,
  CommentIcon,
  CopilotIcon,
  DiffIcon,
  FilterIcon,
  GitCommitIcon,
  GitPullRequestIcon,
  type Icon,
  LightBulbIcon,
  ListUnorderedIcon,
  LogIcon,
  PulseIcon,
  QuestionIcon,
  RocketIcon,
  SearchIcon,
  ShieldCheckIcon,
  SparkleFillIcon,
  TelescopeIcon,
} from '@primer/octicons-react'
import {useCallback, useEffect, useId, useMemo, useRef} from 'react'

const MAX_ICEBREAKERS = 3
const DISMISSED_STORAGE_KEY = 'COPILOT_IMMERSIVE_ICEBREAKERS_DISMISSED'

const SUGGESTION_ICONS: Record<GeneratedSuggestionIconKey, Icon> = {
  ask: QuestionIcon,
  overview: TelescopeIcon,
  'getting-started': RocketIcon,
  summarize: ListUnorderedIcon,
  explain: LightBulbIcon,
  improve: SparkleFillIcon,
  performance: PulseIcon,
  commits: GitCommitIcon,
  changes: DiffIcon,
  review: CodeReviewIcon,
  feedback: CommentIcon,
  'next-steps': ChecklistIcon,
  'create-pr': GitPullRequestIcon,
  logs: LogIcon,
  security: ShieldCheckIcon,
  filter: FilterIcon,
  search: SearchIcon,
  tests: BeakerIcon,
}

interface ChatIcebreakersProps {
  /** Reference type key for selecting contextual suggestions (e.g. 'repository', 'file'). */
  suggestionContext?: string
  onSelectSuggestion: (suggestion: GeneratedSuggestion, event?: Event) => Promise<void>
  onCommit?: (event: Event) => void
}

/**
 * Immersive new-chat icebreakers. Maps the top contextual suggestions to the
 * shared `AgentIcebreakers` panel (with a "Quick prompts" header and dismiss) so
 * the empty state matches the `/copilot/agents` visual style while reusing our
 * chat suggestion content.
 */
export function ChatIcebreakers({suggestionContext = 'default', onSelectSuggestion, onCommit}: ChatIcebreakersProps) {
  const headingId = useId()
  const shownReferenceGroupRef = useRef<string | undefined>(undefined)
  const [dismissed, setDismissed] = useLocalStorage(DISMISSED_STORAGE_KEY, false)

  const suggestions = useMemo(() => {
    const bucket = updatedThreadSuggestions[suggestionContext] ?? updatedThreadSuggestions.default ?? []
    return bucket.slice(0, MAX_ICEBREAKERS)
  }, [suggestionContext])

  const options = useMemo<Array<IcebreakerOption & {suggestion: GeneratedSuggestion}>>(
    () =>
      suggestions.map(suggestion => {
        const IconComponent = suggestion.iconKey ? SUGGESTION_ICONS[suggestion.iconKey] : CopilotIcon
        return {
          title: suggestion.title ?? suggestion.question,
          subheader: suggestion.question,
          icon: <IconComponent size={16} />,
          prompt: suggestion.prompt ?? suggestion.question,
          suggestion,
        }
      }),
    [suggestions],
  )

  useEffect(() => {
    if (dismissed || options.length === 0 || shownReferenceGroupRef.current === suggestionContext) return
    shownReferenceGroupRef.current = suggestionContext
    sendEvent('copilot_generated_suggestions_shown', {referenceGroup: suggestionContext})
  }, [dismissed, options.length, suggestionContext])

  const handleSelect = useCallback(
    (option: IcebreakerOption, event?: Event) => {
      const match = options.find(o => o.title === option.title)
      const suggestion = match?.suggestion
      if (!suggestion) return
      sendEvent('copilot_generated_suggestion_click', {
        intent: suggestion.intent,
        mode: suggestion.mode,
        content: suggestion.question,
        referenceGroup: suggestionContext,
      })
      if (event) void onSelectSuggestion(suggestion, event)
      else void onSelectSuggestion(suggestion)
    },
    [onSelectSuggestion, options, suggestionContext],
  )

  const handleDismiss = useCallback(() => {
    sendEvent('copilot_generated_suggestions_dismissed', {referenceGroup: suggestionContext})
    setDismissed(true)
  }, [setDismissed, suggestionContext])

  if (dismissed || options.length === 0) return null

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="sr-only">
        Suggested prompts
      </h2>
      <AgentIcebreakers
        items={options}
        onSelect={handleSelect}
        onCommit={onCommit}
        onDismiss={handleDismiss}
        heading="Quick prompts"
      />
    </section>
  )
}
